import { createContext, lazy, Suspense, useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BookingModal from "@/components/BookingModal";
import MobileActionBar from "@/components/MobileActionBar";
import Breadcrumbs from "@/components/Breadcrumbs";
import Home from "@/pages/Home";
import useLenis from "@/hooks/useLenis";
import { initAnalytics, trackPageview, trackBooking, trackCall } from "@/lib/analytics";
import { applyPageMeta, setMetaTag } from "@/lib/usePageMeta";

const About = lazy(() => import("@/pages/About"));
const Practice = lazy(() => import("@/pages/Practice"));
const PracticeArea = lazy(() => import("@/pages/PracticeArea"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Contact = lazy(() => import("@/pages/Contact"));
const Resources = lazy(() => import("@/pages/Resources"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const Admin = lazy(() => import("@/pages/Admin"));
const AdminContent = lazy(() => import("@/pages/AdminContent"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const AttorneyPage = lazy(() => import("@/pages/AttorneyPage"));
const ContractorAttorney = lazy(() => import("@/pages/ContractorAttorney"));

export const ThemeContext = createContext({ theme: "dark", toggleTheme: () => {} });

const META_MAP = {
  "/": ["Harrison Litigation Team | Construction, Business & Real Estate Law — Nashville, TN", "Harrison Litigation Team, PLLC — Boutique Nashville law firm delivering big law experience at a personal level. Construction, business & real estate attorneys serving Middle Tennessee."],
  "/about": ["About the Firm | Harrison Litigation Team — Nashville, TN", "Meet the Harrison Litigation Team attorneys — Nashville construction, business, and real estate lawyers with big-firm experience and senior attention on every file."],
  "/services": ["Construction, Business & Real Estate Law | Harrison Litigation Team", "Construction law, business litigation, and real estate counsel for contractors, developers, owners, and investors across Nashville and Middle Tennessee."],
  "/blog": ["Legal Insights & Resources | Harrison Litigation Team — Nashville, TN", "Practical Tennessee legal guides on construction law, mechanic's liens, prompt pay, business disputes, and real estate — from the Harrison Litigation Team."],
  "/contact": ["Contact — Free 15-Minute Consultation | Harrison Litigation Team", "Book a free 15-minute consultation with a Nashville construction, business, or real estate attorney. Call (615) 852-7141 or schedule online."],
};

const ScrollToTop = () => {
  const { pathname, search } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    initAnalytics();
    trackPageview(pathname + search);
    const m = META_MAP[pathname];
    if (m) {
      applyPageMeta(m[0], m[1]);
      setMetaTag("name", "robots", "index, follow");
    }
  }, [pathname, search]);
  return null;
};

// Legacy/alternate biography URL patterns (referenced in firm bio documents) → canonical /about/<slug>
const BioAlias = () => {
  const { slug } = useParams();
  return <Navigate to={`/about/${slug}`} replace />;
};


function App() {
  const [theme, setTheme] = useState(() => localStorage.getItem("hlt-theme-v2") || "dark");
  useLenis();

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("hlt-theme-v2", theme);
  }, [theme]);

  useEffect(() => {
    const onMessage = (e) => {
      if (e?.data?.event === "calendly.event_scheduled") trackBooking();
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      const link = e.target.closest?.('a[href^="tel:"]');
      if (link) trackCall(link.getAttribute("href"));
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <BrowserRouter>
        <ScrollToTop />
        <div className="grain min-h-screen bg-background text-foreground font-sans antialiased">
          <Navbar />
          <Breadcrumbs />
          <MobileActionBar />
          <main>
            <Suspense fallback={<div className="min-h-screen bg-background" />}>
              <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Practice />} />
              <Route path="/services/:areaId" element={<PracticeArea />} />
              <Route path="/construction/contractor-attorney" element={<ContractorAttorney />} />
              <Route path="/construction" element={<Navigate to="/services/construction" replace />} />
              <Route path="/business" element={<Navigate to="/services/business" replace />} />
              <Route path="/real-estate" element={<Navigate to="/services/realestate" replace />} />
              <Route path="/about-us" element={<Navigate to="/about" replace />} />
              <Route path="/contact-us" element={<Navigate to="/contact" replace />} />
              <Route path="/book-a-consultation" element={<Navigate to="/contact" replace />} />
              <Route path="/about/:slug" element={<AttorneyPage />} />
              <Route path="/jeanne-harrison-1" element={<Navigate to="/about/jeanne-harrison" replace />} />
              <Route path="/jeanne-harrison" element={<Navigate to="/about/jeanne-harrison" replace />} />
              <Route path="/ryan-harrison" element={<Navigate to="/about/ryan-harrison" replace />} />
              <Route path="/donna-mintz" element={<Navigate to="/about/donna-mintz" replace />} />
              <Route path="/benjamin-adams" element={<Navigate to="/about/benjamin-adams" replace />} />
              <Route path="/reviews" element={<Navigate to="/about" replace />} />
              <Route path="/privacy-policy" element={<LegalPage kind="privacy" />} />
              <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
              <Route path="/terms-of-service" element={<LegalPage kind="terms" />} />
              <Route path="/terms" element={<Navigate to="/terms-of-service" replace />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/quick-guides-1" element={<Navigate to="/resources" replace />} />
              <Route path="/upcoming-events" element={<Navigate to="/resources#events" replace />} />
              <Route path="/blog-posts" element={<Navigate to="/blog" replace />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin/content" element={<AdminContent />} />
              <Route path="/attorneys/:slug" element={<BioAlias />} />
              <Route path="/team/:slug" element={<BioAlias />} />
              <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </main>
          <Footer />
        </div>
        <BookingModal />
        <Toaster theme={theme} position="bottom-right" />
      </BrowserRouter>
    </ThemeContext.Provider>
  );
}

export default App;
